import {
  Box,
  Collapse,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Stack,
  Tag,
  TagLabel,
  TagCloseButton,
  useDisclosure,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Switch,
  FormControl,
  FormLabel,
  Portal,
  Text,
  Wrap,
  WrapItem,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import { IoChevronBack, IoSettingsOutline, IoSearch, IoRefreshOutline, IoAlertCircleOutline } from 'react-icons/io5';
import { HiOutlineAdjustmentsHorizontal } from 'react-icons/hi2';
import { BsLayoutSidebarReverse, BsTwitterX } from 'react-icons/bs';
import { FaInstagram, FaTiktok, FaYoutube, FaFacebook } from 'react-icons/fa';
import { SiSubstack } from 'react-icons/si';
import { IoClose } from 'react-icons/io5';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowRight,
  MdOutlinePeople,
  MdOutlinePerson,
  MdOutlineSmartToy,
  MdRefresh,
} from 'react-icons/md';
import { RiFile2Line, RiShare2Line, RiTerminalBoxLine, RiRobot2Line } from 'react-icons/ri';
import { RxPencil2 } from 'react-icons/rx';
import { TbWorld } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import {
  Comparison,
  ComparisonType,
  ConversationType,
  DigitalTwin,
  PerspectiveBaseInfoFragment,
  Project,
  ProjectDocument,
  ProjectStatus,
  ProjectType,
  Subtopic,
  useGetPerspectiveByDtTopicProjectLazyQuery,
  useMarkAsViewedMutation,
  useProjectQuery,
  useGetProjectSettingsQuery,
  useTriggerProjectUpdateMutation,
  useRetryProjectMutation,
  ProjectTemplatesDocument,
} from '../../generated/graphql';
import { getIndustryDetails } from '../../utils/industryDetails';
import { formatDateShort } from '../../utils/formatDate';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanLoading } from '../HelivanLoading';
import { DemographicFilterSelect } from './DemographicFilterSelect';
import { ProjectTabViewChat } from './ProjectTabViewChat';
import { PlanningInspect } from './PlanningInspect';
import { PersonalInspect } from './PersonalInspect';
import { SteerHub } from './SteerHub';
import { ProjectTabViewOverall } from './ProjectTabViewOverall';
import { DigitalTwinMetadata } from '../DigitalTwins/DigitalTwinMetadata';
import { HelivanButton } from '../helivan/HelivanButton';
import { AddYourselfModal } from './AddYourselfModal';
import { TakeActionModal } from './TakeActionModal';
import { DownloadReportModal } from './DownloadReportModal';
import { ProjectSettingsModal } from './ProjectSettingsModal';
import { ShareProjectModal } from './ShareProjectModal';
import { AddAgentToProjectModal } from './AddAgentToProjectModal';
import { useStatsigClient } from '@statsig/react-bindings';
import { useAuth } from '../../contexts/AuthContext';
import { useTour } from '../Tour/TourContext';

export type ModelCardData = {
  digitalTwin?: DigitalTwin;
  customAgent?: { id: string; name: string };
  entityId: string;
  entityName: string;
  similar: Comparison[];
  dissimilar: Comparison[];
  questions: { question: string; answer: string }[];
  topic: string;
  projectId: string;
  // Planning-probe signals for the selected (agent, task). Null for opinion watches.
  qualityScore?: number | null;
  judgeNotes?: string | null;
};

type ProjectViewType = 'overall' | 'chat' | 'faq' | 'steer';

// Helper to get entity info from perspective (either digitalTwin or customAgent)
function getEntityInfo(perspective: PerspectiveBaseInfoFragment): {
  id: string;
  name: string;
} {
  if (perspective.digitalTwin) {
    return { id: perspective.digitalTwin.id, name: perspective.digitalTwin.name };
  }
  if (perspective.customAgent) {
    return { id: perspective.customAgent.id, name: perspective.customAgent.name };
  }
  return { id: 'unknown', name: 'Unknown' };
}

export const ProjectView = ({ project }: { project: Project }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paramConversationId = queryParams.get('conversationId');
  const { backendUser, isAuthenticated } = useAuth();

  const { data, loading, refetch: refetchProject } = useProjectQuery({
    variables: { id: project.id },
    fetchPolicy: 'cache-first', // Use cached data if available for faster loads
    nextFetchPolicy: 'cache-and-network', // Revalidate in background
  });
  const { data: settingsData } = useGetProjectSettingsQuery({
    variables: { projectTemplateId: project.projectTemplateId! },
    skip: !project.projectTemplateId,
    fetchPolicy: 'cache-first',
  });
  const [getPerspectiveByDt] = useGetPerspectiveByDtTopicProjectLazyQuery();
  const [viewType, setViewType] = useState<ProjectViewType>(
    paramConversationId != null ? 'chat' : 'overall'
  );
  const [chatFilter, setChatFilter] = useState<ConversationType>(
    ConversationType.Persona
  );

  // Use useMemo for derived data to prevent recalculations
  // Get unique entities (perspectives may have duplicates across subtopics)
  const allDigitalTwins = useMemo(() => {
    const uniqueTwins = new Map<string, { name: string; id: string }>();
    (data?.project?.perspectives ?? []).forEach((perspective) => {
      const entity = getEntityInfo(perspective);
      if (!uniqueTwins.has(entity.id)) {
        uniqueTwins.set(entity.id, {
          name: entity.name,
          id: entity.id,
        });
      }
    });
    return Array.from(uniqueTwins.values());
  }, [data?.project?.perspectives]);

  // Set of custom agent IDs already in the project (from perspectives)
  const existingAgentIds = useMemo(() => {
    const ids = new Set<string>();
    (data?.project?.perspectives ?? []).forEach((p) => {
      if (p.customAgent?.id) ids.add(p.customAgent.id);
    });
    return ids;
  }, [data?.project?.perspectives]);

  const [digitalTwinId, setDigitalTwinId] = useState<string | undefined>();
  const [modelCardData, setModelCardData] = useState<ModelCardData>();
  const [selectedTwins, setSelectedTwins] = useState<Set<{ id: string; name: string }>>(new Set());
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);

  // For projects with 8 or fewer agents, pre-select all agents and default to Timeline view
  // Use >= 1 so single-agent projects default to Timeline (All Perspectives is useless with 1 dot)
  const projectHasTimeline = project.timelineProjects && project.timelineProjects.length >= 1;
  // Can manage the watch: template owner or an org admin of the watch's org
  // (computed server-side, works on every timeline version), OR the creator of
  // this specific run (covers one-off projects with no template).
  const ownsWatch = !!project.canManageWatch || !!project.createdByYou;
  // Agents added but not yet generated — included on the next update.
  const stagedAgents = data?.project?.stagedAgents ?? project.stagedAgents ?? [];
  const shouldPreSelectAll = allDigitalTwins.length > 0 && allDigitalTwins.length <= 8 && projectHasTimeline;
  // Timeline is now tab index 0, All Perspectives is tab index 1
  const initialTabIndex = shouldPreSelectAll ? 0 : 1;

  // Pre-select all agents for small projects with timeline
  useEffect(() => {
    if (shouldPreSelectAll && !hasInitializedSelection && allDigitalTwins.length > 0) {
      const allTwinsSet = new Set(allDigitalTwins.map(twin => ({ id: twin.id, name: twin.name })));
      setSelectedTwins(allTwinsSet);
      setHasInitializedSelection(true);
    }
  }, [shouldPreSelectAll, hasInitializedSelection, allDigitalTwins]);

  // Updated from single demographic to set of demographics
  const [selectedDemographics, setSelectedDemographics] = useState<
    Set<{ key: string; value: string }> | undefined
  >();

  // Persona search
  const [personaSearchQuery, setPersonaSearchQuery] = useState('');
  const [showPersonaDropdown, setShowPersonaDropdown] = useState(false);
  const [showAllPersonas, setShowAllPersonas] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const emptySearchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Filter personas based on search query
  const filteredPersonas = useMemo(() => {
    if (!personaSearchQuery.trim()) {
      // Show all personas if timer has elapsed
      return showAllPersonas ? allDigitalTwins : [];
    }
    const query = personaSearchQuery.toLowerCase();
    return allDigitalTwins.filter((twin) =>
      twin.name.toLowerCase().includes(query)
    );
  }, [allDigitalTwins, personaSearchQuery, showAllPersonas]);

  // Handle empty search timer - show all personas after 2 seconds
  useEffect(() => {
    if (showPersonaDropdown && !personaSearchQuery.trim()) {
      emptySearchTimerRef.current = setTimeout(() => {
        setShowAllPersonas(true);
      }, 2000);
    } else {
      setShowAllPersonas(false);
    }

    return () => {
      if (emptySearchTimerRef.current) {
        clearTimeout(emptySearchTimerRef.current);
      }
    };
  }, [showPersonaDropdown, personaSearchQuery]);

  const [perspectives, setPerspectives] =
    useState<PerspectiveBaseInfoFragment[]>();
  const [subtopics, setSubtopics] = useState<Subtopic[]>();
  const navigate = useNavigate();
  const [markViewed] = useMarkAsViewedMutation();
  const { maybeAutoStartWatchTour } = useTour();

  // Mark project as viewed on load. Skipped for signed-out visitors — markViewed
  // is a mutation and would be rejected for anonymous users.
  useEffect(() => {
    if (!isAuthenticated) return;
    markViewed({
      variables: {
        id: project.id,
      },
    });
  }, [project.id, markViewed, isAuthenticated]);

  // First time an authenticated user opens a watch, run the watch-detail tour
  // (no-op if they've already seen it).
  useEffect(() => {
    if (isAuthenticated) maybeAutoStartWatchTour();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Update perspectives and subtopics when data changes
  useEffect(() => {
    if (data?.project?.perspectives) {
      setPerspectives(data.project.perspectives);
    }
    if (data?.project?.subtopics) {
      setSubtopics(data.project.subtopics);
    }
  }, [data?.project?.perspectives, data?.project?.subtopics]); // Only dependencies we need

  // Color palette for selected personas (must match ProjectPlot.tsx)
  const PERSONA_COLORS = useMemo(() => [
    '#c95149', // red
    '#4CAF50', // green
    '#2196F3', // blue
    '#FF9800', // orange
    '#9C27B0', // purple
    '#00BCD4', // cyan
    '#E91E63', // pink
    '#795548', // brown
  ], []);

  // Get full entity data for selected personas (for sidebar display)
  // Preserves order from selectedTwins to match colors
  const selectedTwinsWithData = useMemo(() => {
    if (!perspectives || selectedTwins.size === 0) return [];

    // Create a map for quick lookup - store the perspective to access either digitalTwin or customAgent
    const perspectivesMap = new Map<string, (typeof perspectives)[0]>();
    perspectives.forEach((p) => {
      const entity = getEntityInfo(p);
      if (!perspectivesMap.has(entity.id)) {
        perspectivesMap.set(entity.id, p);
      }
    });

    // Return in the same order as selectedTwins
    const result: Array<{ id: string; name: string; color: string; digitalTwin?: (typeof perspectives)[0]['digitalTwin']; customAgent?: (typeof perspectives)[0]['customAgent'] }> = [];
    Array.from(selectedTwins).forEach((twin, index) => {
      const perspective = perspectivesMap.get(twin.id);
      if (perspective) {
        const entity = getEntityInfo(perspective);
        result.push({
          id: entity.id,
          name: entity.name,
          color: PERSONA_COLORS[index % PERSONA_COLORS.length],
          digitalTwin: perspective.digitalTwin ?? undefined,
          customAgent: perspective.customAgent ?? undefined,
        });
      }
    });
    return result;
  }, [perspectives, selectedTwins, PERSONA_COLORS]);

  // Memoize the query parameters to prevent unnecessary effect triggers
  const perspectiveQueryParams = useMemo(() => {
    return digitalTwinId
      ? {
          dtId: digitalTwinId,
          projectId: project.id,
          topic: 'topic', // todo fix this
        }
      : null;
  }, [digitalTwinId, project.id]);

  // Fetch perspective data when digitalTwinId changes
  useEffect(() => {
    if (perspectiveQueryParams) {
      getPerspectiveByDt({
        variables: perspectiveQueryParams,
        onCompleted: (data) => {
          const perspective = data.getPerspectiveByDtTopicProject;
          const entityName = perspective.digitalTwin?.name ?? perspective.customAgent?.name ?? 'Unknown';
          const entityId = perspective.digitalTwin?.id ?? perspective.customAgent?.id ?? 'unknown';
          setModelCardData({
            digitalTwin: perspective.digitalTwin ?? undefined,
            customAgent: perspective.customAgent ?? undefined,
            entityId,
            entityName,
            similar: (perspective.comparisons ?? []).filter((c) => c.type === ComparisonType.Similar),
            dissimilar: (perspective.comparisons ?? []).filter((c) => c.type === ComparisonType.Dissimilar),
            questions: (perspective.opinions ?? []).map(
              (opinion) => {
                return {
                  question: opinion.question,
                  answer: opinion.answer,
                };
              }
            ),
            topic: `${entityName} on ${perspective.topic === 'topic' ? project.topic : perspective.topic}`,
            projectId: project.id,
            qualityScore: perspective.qualityScore ?? null,
            judgeNotes: perspective.judgeNotes ?? null,
          });
        },
      });
    } else {
      setModelCardData(undefined);
      setDigitalTwinId(undefined);
    }
  }, [perspectiveQueryParams, getPerspectiveByDt, project.id, project.topic]);

  // Memoize callback to pass to children
  const handleSetDigitalTwinId = useCallback((id: string | undefined) => {
    setDigitalTwinId(id);
  }, []);

  const handleSetSelectedTwins = useCallback(
    (newSelection: React.SetStateAction<Set<{ id: string; name: string }>>) => {
      setSelectedTwins(newSelection);
    },
    []
  );

  const selectedTwinIds = useMemo(() => {
    const idSet = new Set<string>();
    selectedTwins.forEach((twin) => idSet.add(twin.id));
    return idSet;
  }, [selectedTwins]);

  // Update the complex Set when the simple ID Set changes
  const handleSelectedTwinIdsChange = useCallback(
    (newIdSet: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      // Handle both direct Set assignment and function updates
      const resolvedIdSet =
        typeof newIdSet === 'function' ? newIdSet(selectedTwinIds) : newIdSet;
      const newTwinSet = new Set<{ id: string; name: string }>();

      // Create a map for quick ID to name lookup
      const idToNameMap = new Map<string, string>();
      allDigitalTwins.forEach((twin) => idToNameMap.set(twin.id, twin.name));

      // Add all selected twins to the new set
      resolvedIdSet.forEach((id) => {
        const name = idToNameMap.get(id);
        if (name) {
          newTwinSet.add({ id, name });
        }
      });

      setSelectedTwins(newTwinSet);
    },
    [allDigitalTwins, setSelectedTwins, selectedTwinIds]
  );

  const [activeSection, setActiveSection] = useState<string>('overall');

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const plotRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const scrollToPlot = useCallback((plotId: string) => {
    const plotElement = plotRefs.current[plotId];
    if (plotElement && scrollContainerRef.current) {
      plotElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const setPlotRef = useCallback(
    (plotId: string, element: HTMLDivElement | null) => {
      plotRefs.current[plotId] = element;
    },
    []
  );

  // Add Intersection Observer to track visible sections
  useEffect(() => {
    if (!scrollContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Get the ID from the observed element
            const plotId = entry.target.getAttribute('data-plot-id');
            if (plotId) {
              setActiveSection(plotId);
            }
          }
        });
      },
      {
        root: scrollContainerRef.current,
        threshold: 0.5, // Trigger when 50% of the section is visible
      }
    );

    // Observe all plot sections
    Object.entries(plotRefs.current).forEach(([id, element]) => {
      if (element) {
        element.setAttribute('data-plot-id', id);
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [subtopics]); // Re-run when subtopics change

  const industryDetails = getIndustryDetails(project.digitalTwinType);
  // Header icon, matching the watch-list cards: terminal (coding agent), robot
  // (personal agent — your own CustomAgent), or the persona's industry icon.
  const headerCustomAgentName = useMemo(() => {
    for (const p of data?.project?.perspectives ?? []) {
      if (p.customAgent?.name) return p.customAgent.name;
    }
    return null;
  }, [data?.project?.perspectives]);
  const isCodingWatch = project.type === ProjectType.PlanningProbe;
  const isPersonalAgentWatch = !isCodingWatch && !!headerCustomAgentName;
  // Steering tools apply to agent watches (coding or personal agent), not persona watches.
  const isAgentWatch = isCodingWatch || isPersonalAgentWatch;
  const headerIcon = isCodingWatch
    ? RiTerminalBoxLine
    : isPersonalAgentWatch
      ? RiRobot2Line
      : industryDetails.industryDetails.icon;
  const mapCollapse = useDisclosure();

  const filteredChats = (data?.getRecentConversationsBasicData ?? []).filter(
    (chat) => chat.type === chatFilter
  );
  const [conversationId, setConversationId] = useState<string | undefined>(
    paramConversationId != null ? paramConversationId : undefined
  );
  const [createNewChat, setCreateNewChat] = useState<boolean>(false);
  const addYourselfModal = useDisclosure();
  const takeActionModal = useDisclosure();
  const downloadReportModal = useDisclosure();
  const projectSettingsModal = useDisclosure();
  const shareProjectModal = useDisclosure();
  const updateConfirmModal = useDisclosure();
  const addAgentModal = useDisclosure();

  const toast = useToast();

  const [triggerProjectUpdate, { loading: updateLoading }] = useTriggerProjectUpdateMutation({
    refetchQueries: [{ query: ProjectTemplatesDocument }],
  });

  const [retryProject, { loading: retryLoading }] = useRetryProjectMutation({
    refetchQueries: [{ query: ProjectTemplatesDocument }],
  });

  const handleUpdateProject = async () => {
    if (!project.projectTemplateId) {
      toast({
        title: 'Cannot update',
        description: 'This project does not have a template associated with it.',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    try {
      const result = await triggerProjectUpdate({
        variables: {
          input: {
            projectTemplateId: project.projectTemplateId,
          },
        },
      });

      if (result.data?.triggerProjectUpdate?.id) {
        toast({
          title: 'Update started',
          description: 'A new version of this watch is being generated.',
          status: 'success',
          duration: 5000,
        });
        updateConfirmModal.onClose();
        // Navigate to the new project
        navigate(`/projects/${result.data.triggerProjectUpdate.id}`);
      }
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error instanceof Error ? error.message : 'Failed to trigger update',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const { checkGate } = useStatsigClient();
  const showAddYourself = checkGate('show_add_yourself');
  const showActionsModal = checkGate('show_actions_modal');

  // Sidebar visibility state - hidden by default
  const [showSidebar, setShowSidebar] = useState(false);

  // Filters visibility state - hidden by default
  const [showFilters, setShowFilters] = useState(false);

  // Timeline comparison state
  const hasTimeline =
    project.timelineProjects && project.timelineProjects.length > 1;
  const [showMovement, setShowMovement] = useState(false);

  // Default to first project that ISN'T the current project (oldest non-current)
  const [comparisonProjectId, setComparisonProjectId] = useState<
    string | undefined
  >(() => {
    if (
      hasTimeline &&
      project.timelineProjects &&
      project.timelineProjects.length > 0
    ) {
      const firstNonCurrent = project.timelineProjects.find(
        (tp) => tp.id !== project.id
      );
      return firstNonCurrent?.id;
    }
    return undefined;
  });

  // Reset comparison state when project changes (e.g., navigating timeline)
  useEffect(() => {
    const hasTimelineNow =
      project.timelineProjects && project.timelineProjects.length > 1;
    setShowMovement(false); // Always default to off

    if (
      hasTimelineNow &&
      project.timelineProjects &&
      project.timelineProjects.length > 0
    ) {
      const firstNonCurrent = project.timelineProjects.find(
        (tp) => tp.id !== project.id
      );
      setComparisonProjectId(firstNonCurrent?.id);
    } else {
      setComparisonProjectId(undefined);
    }
  }, [project.id, project.timelineProjects]);

  // Fetch comparison project data
  const { data: comparisonProjectData } = useProjectQuery({
    variables: { id: comparisonProjectId ?? '' },
    skip: !comparisonProjectId || !showMovement,
    fetchPolicy: 'cache-first', // Use cached data if available
  });

  // Fetch ALL timeline projects for Timeline view - DEFERRED until needed
  const apolloClient = useApolloClient();
  const [allTimelinePerspectives, setAllTimelinePerspectives] = useState<
    Map<string, PerspectiveBaseInfoFragment[]>
  >(new Map());
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [timelineDataRequested, setTimelineDataRequested] = useState(false);

  // Function to trigger timeline data loading
  const requestTimelineData = useCallback(() => {
    if (!timelineDataRequested && hasTimeline) {
      setTimelineDataRequested(true);
    }
  }, [timelineDataRequested, hasTimeline]);

  // Only fetch timeline data when explicitly requested (by showMovement or Timeline tab)
  useEffect(() => {
    if (showMovement && hasTimeline && !timelineDataRequested) {
      setTimelineDataRequested(true);
    }
  }, [showMovement, hasTimeline, timelineDataRequested]);

  useEffect(() => {
    if (
      timelineDataRequested &&
      hasTimeline &&
      project.timelineProjects &&
      project.timelineProjects.length > 0 &&
      allTimelinePerspectives.size === 0
    ) {
      setLoadingTimeline(true);

      // Fetch all timeline projects in parallel with cache optimization
      const fetchPromises = project.timelineProjects.map((timelineProject) =>
        apolloClient
          .query({
            query: ProjectDocument,
            variables: { id: timelineProject.id },
            fetchPolicy: 'cache-first',
          })
          .then((result) => ({
            projectId: timelineProject.id,
            perspectives: result.data.project?.perspectives ?? [],
          }))
          .catch((error) => {
            console.error(
              `Failed to fetch timeline project ${timelineProject.id}:`,
              error
            );
            return {
              projectId: timelineProject.id,
              perspectives: [] as PerspectiveBaseInfoFragment[],
            };
          })
      );

      Promise.all(fetchPromises).then((results) => {
        const perspectivesMap = new Map<
          string,
          PerspectiveBaseInfoFragment[]
        >();
        results.forEach(({ projectId, perspectives }) => {
          perspectivesMap.set(projectId, perspectives);
        });
        setAllTimelinePerspectives(perspectivesMap);
        setLoadingTimeline(false);
      });
    }
  }, [apolloClient, hasTimeline, project.timelineProjects, project.id, timelineDataRequested, allTimelinePerspectives.size]);

  return loading ? (
    <HelivanLoading />
  ) : (
    <Flex
      width="full"
      height="full"
      direction="column"
      overflow="hidden"
      bg={HelivanColor.background.primary}
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      borderRadius="8px"
    >
      {/* Project View Header */}
      <AddYourselfModal
        isOpen={addYourselfModal.isOpen}
        onClose={addYourselfModal.onClose}
        questions={project.questions ?? []}
        projectTitle={project.topic}
        projectId={project.id}
      />
      <TakeActionModal
        isOpen={takeActionModal.isOpen}
        onClose={takeActionModal.onClose}
        allDigitalTwins={allDigitalTwins}
        subtopics={subtopics}
      />
      <DownloadReportModal
        isOpen={downloadReportModal.isOpen}
        onClose={downloadReportModal.onClose}
        subtopics={subtopics ?? []}
        perspectives={perspectives ?? []}
        projectTopic={project.topic}
        dtType={project.digitalTwinType}
        questions={project.questions ?? []}
        projectId={project.id}
      />
      <ProjectSettingsModal
        isOpen={projectSettingsModal.isOpen}
        onClose={projectSettingsModal.onClose}
        projectTemplateId={project.projectTemplateId!}
        projectName={project.topic}
      />
      <ShareProjectModal
        isOpen={shareProjectModal.isOpen}
        onClose={shareProjectModal.onClose}
        projectId={project.id}
        projectName={project.topic}
        organizationId={backendUser?.currentOrganization?.id ?? ''}
        currentUserId={backendUser?.id ?? ''}
      />

      <AddAgentToProjectModal
        isOpen={addAgentModal.isOpen}
        onClose={addAgentModal.onClose}
        projectId={project.id}
        existingAgentIds={existingAgentIds}
        onAgentAdded={() => refetchProject()}
      />

      {/* Update Confirmation Modal */}
      <Modal isOpen={updateConfirmModal.isOpen} onClose={updateConfirmModal.onClose} isCentered>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="16px" mx={4} overflow="hidden">
          <Box bg={HelivanColor.background.secondary} borderBottom="1px solid" borderColor={HelivanColor.border.primary} px={6} py={4}>
            <ModalHeader p={0} color={HelivanColor.text.blueDark} fontSize="lg" fontWeight="600">
              <HStack spacing={2}>
                <IoRefreshOutline size={20} />
                <Text>Update watch</Text>
              </HStack>
            </ModalHeader>
          </Box>

          <ModalBody py={6} px={6}>
            <Stack spacing={4}>
              <Text color={HelivanColor.text.blueDark} fontSize="md">
                You are about to create a new version of:
              </Text>
              <Box
                bg={HelivanColor.background.secondary}
                p={4}
                borderRadius="12px"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
              >
                <Text fontWeight="600" color={HelivanColor.text.blueDark}>
                  {project.topic}
                </Text>
              </Box>
              <Text color="gray.600" fontSize="sm">
                This will generate fresh perspectives using the same configuration. The current version will remain available in the timeline.
              </Text>
            </Stack>
          </ModalBody>

          <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.200" gap={3}>
            <Button
              variant="outline"
              onClick={updateConfirmModal.onClose}
              isDisabled={updateLoading}
              size="md"
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateProject}
              isLoading={updateLoading}
              loadingText="Triggering..."
              size="md"
              leftIcon={<IoRefreshOutline />}
            >
              Update Now
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Project Header */}
      <Flex
        width="full"
        alignItems="center"
        justifyContent="space-between"
        py={{ base: 2, md: 3 }}
        px={{ base: 3, md: 5 }}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        bg="white"
        flexWrap={{ base: 'wrap', lg: 'nowrap' }}
        gap={{ base: 2, md: 0 }}
      >
        <HStack spacing={{ base: 2, md: 3 }} flex={{ base: '1 1 auto', lg: '0 0 auto' }} minWidth={0}>
          <Icon
            as={headerIcon}
            height={{ base: '20px', md: '24px' }}
            width={{ base: '20px', md: '24px' }}
            color={HelivanColor.text.helivanBlue}
            flexShrink={0}
          />
          <HStack spacing={2} alignItems="baseline" minWidth={0} flex={1}>
            <Text
              fontSize={{ base: '13px', md: '15px' }}
              fontWeight={600}
              color={HelivanColor.text.blueDark}
              noOfLines={1}
              maxWidth={{ base: '180px', sm: '250px', md: '400px' }}
            >
              {project.topic}
            </Text>
            {project.createdAt && (
              <Text fontSize={{ base: '10px', md: '11px' }} color={HelivanColor.text.gray} display={{ base: 'none', sm: 'block' }}>
                {formatDateShort(project.createdAt)}
              </Text>
            )}
          </HStack>
        </HStack>

        {/* Timeline dots - hidden on mobile, visible on tablet+ */}
        {project.timelineProjects && project.timelineProjects.length > 1 && (
          <HStack data-tour="watch-timeline-dots" spacing={0} flex={1} justifyContent="center" maxWidth="400px" mx={4} display={{ base: 'none', md: 'flex' }}>
            <Box
              position="relative"
              height="32px"
              width="100%"
              display="flex"
              alignItems="center"
            >
              {/* Timeline line */}
              <Box
                position="absolute"
                top="50%"
                left="0"
                right="0"
                height="2px"
                bg={HelivanColor.border.primary}
                transform="translateY(-50%)"
              />

              {/* Timeline points */}
              {project.timelineProjects.map((timelineProject, index) => {
                const isCurrent = timelineProject.id === project.id;
                const position =
                  project.timelineProjects!.length === 1
                    ? 50
                    : (index / (project.timelineProjects!.length - 1)) * 100;

                return (
                  <Tooltip
                    key={timelineProject.id}
                    label={formatDateShort(timelineProject.dateCreated)}
                    placement="top"
                    hasArrow
                  >
                    <Box
                      position="absolute"
                      left={`${position}%`}
                      transform="translateX(-50%)"
                      cursor="pointer"
                      onClick={() => navigate(`/projects/${timelineProject.id}`)}
                      zIndex={1}
                    >
                      <Box
                        width={isCurrent ? '12px' : '8px'}
                        height={isCurrent ? '12px' : '8px'}
                        borderRadius="50%"
                        bg={isCurrent ? HelivanColor.text.helivanBlue : 'white'}
                        border="2px solid"
                        borderColor={HelivanColor.text.helivanBlue}
                        transition="all 0.2s"
                        _hover={{ transform: 'scale(1.3)' }}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </HStack>
        )}

        <HStack spacing={{ base: 1, md: 2 }} flexWrap="wrap" justifyContent="flex-end">
          {/* Filters toggle */}
          <Tooltip label="Filters" placement="bottom">
            <IconButton
              icon={<HiOutlineAdjustmentsHorizontal size={18} />}
              aria-label="Toggle filters"
              variant={showFilters ? 'solid' : 'ghost'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              color={showFilters ? 'white' : HelivanColor.text.blueMid}
              bg={showFilters ? HelivanColor.text.helivanBlue : 'transparent'}
              _hover={{ bg: showFilters ? HelivanColor.text.blueDark : HelivanColor.background.secondary }}
            />
          </Tooltip>

          {/* Sidebar toggle */}
          <Tooltip label="Details panel" placement="bottom">
            <IconButton
              icon={<BsLayoutSidebarReverse size={18} />}
              aria-label="Toggle sidebar"
              variant={showSidebar ? 'solid' : 'ghost'}
              size="sm"
              onClick={() => setShowSidebar(!showSidebar)}
              color={showSidebar ? 'white' : HelivanColor.text.blueMid}
              bg={showSidebar ? HelivanColor.text.helivanBlue : 'transparent'}
              _hover={{ bg: showSidebar ? HelivanColor.text.blueDark : HelivanColor.background.secondary }}
            />
          </Tooltip>

          <Box height="20px" width="1px" bg={HelivanColor.border.primary} mx={1} />

          {/* Settings button - show if the viewer owns the watch (any version) and it has a template */}
          {ownsWatch && project.projectTemplateId && (
            <Tooltip label="Settings" placement="bottom">
              <IconButton
                data-tour="watch-settings-btn"
                icon={<IoSettingsOutline size={18} />}
                aria-label="Project settings"
                variant="ghost"
                size="sm"
                onClick={projectSettingsModal.onOpen}
                color={HelivanColor.text.blueMid}
                _hover={{ bg: HelivanColor.background.secondary }}
              />
            </Tooltip>
          )}

          {/* Add Agent button - show if the viewer owns the watch (any version) */}
          {ownsWatch && (
            <Tooltip label="Add Agent" placement="bottom">
              <IconButton
                data-tour="watch-add-agent-btn"
                icon={<MdOutlineSmartToy size={18} />}
                aria-label="Add agent to project"
                variant="ghost"
                size="sm"
                onClick={addAgentModal.onOpen}
                color={HelivanColor.text.blueMid}
                _hover={{ bg: HelivanColor.background.secondary }}
              />
            </Tooltip>
          )}

          {/* Update Now button - show if the viewer owns the watch (any version) and it has a template */}
          {ownsWatch && project.projectTemplateId && (
            <Tooltip label="Update Now" placement="bottom">
              <IconButton
                data-tour="watch-update-btn"
                icon={<MdRefresh size={18} />}
                aria-label="Update project"
                variant="ghost"
                size="sm"
                onClick={updateConfirmModal.onOpen}
                color={HelivanColor.text.blueMid}
                _hover={{ bg: HelivanColor.background.secondary }}
                isLoading={updateLoading}
              />
            </Tooltip>
          )}

          {isAuthenticated && (
            <Tooltip label="Share" placement="bottom">
              <IconButton
                icon={<RiShare2Line size={18} />}
                aria-label="Share"
                variant="ghost"
                size="sm"
                color={HelivanColor.text.blueMid}
                _hover={{ bg: HelivanColor.background.secondary }}
                onClick={shareProjectModal.onOpen}
              />
            </Tooltip>
          )}
        </HStack>
      </Flex>

      {/* Staged-agents banner: agents added but not yet generated */}
      {ownsWatch && project.projectTemplateId && stagedAgents.length > 0 && (
        <Flex
          width="full"
          px={{ base: 3, md: 5 }}
          py={2}
          bg="#FFF7ED"
          borderBottom="1px solid"
          borderBottomColor="#FED7AA"
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          flexWrap="wrap"
        >
          <HStack spacing={2} flex="1" minW={0}>
            <IoAlertCircleOutline size={16} color="#C2410C" />
            <Text fontSize="13px" color="#9A3412" noOfLines={2}>
              {stagedAgents.length === 1
                ? `${stagedAgents[0].name} is staged`
                : `${stagedAgents.length} agents are staged`}{' '}
              for the next update{stagedAgents.length <= 3
                ? `: ${stagedAgents.map((a) => a.name).join(', ')}`
                : ''}.
            </Text>
          </HStack>
          <Button
            size="xs"
            leftIcon={<MdRefresh size={14} />}
            bg={HelivanColor.text.helivanBlue}
            color="white"
            _hover={{ bg: HelivanColor.hover.blueDarker }}
            borderRadius="8px"
            isLoading={updateLoading}
            onClick={handleUpdateProject}
            flexShrink={0}
          >
            Update now
          </Button>
        </Flex>
      )}

      {/* Main content area */}
      <Flex width="full" flex={1} overflow="hidden" position="relative">
        {/* Left side body */}
        <Stack width="full" spacing={0} overflow="hidden" flex={1}>
          {/* Compact toolbar with view tabs */}
          <Flex
            px={{ base: 3, md: 5 }}
            py={{ base: 2, md: 3 }}
            borderBottom="1px solid"
            borderBottomColor={HelivanColor.border.primary}
            alignItems="center"
            justifyContent="space-between"
            bg="white"
            flexWrap={{ base: 'wrap', sm: 'nowrap' }}
            gap={{ base: 2, sm: 0 }}
          >
            {/* View tabs - pill style */}
            <HStack
              data-tour="watch-view-tabs"
              spacing={1}
              bg={HelivanColor.background.secondary}
              p={1}
              borderRadius="8px"
            >
              <Box
                onClick={() => {
                  setViewType('overall');
                  setConversationId(undefined);
                }}
                px={{ base: 3, sm: 4 }}
                py={1.5}
                borderRadius="6px"
                bg={viewType === 'overall' ? 'white' : 'transparent'}
                boxShadow={viewType === 'overall' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'}
                cursor="pointer"
                transition="all 0.15s ease"
                _hover={{ bg: viewType === 'overall' ? 'white' : 'rgba(255,255,255,0.5)' }}
              >
                <Text
                  fontSize="13px"
                  fontWeight={viewType === 'overall' ? 600 : 500}
                  color={viewType === 'overall' ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid}
                >
                  Monitor
                </Text>
              </Box>
              <Box
                onClick={() => {
                  setViewType('faq');
                  setConversationId(undefined);
                }}
                px={{ base: 3, sm: 4 }}
                py={1.5}
                borderRadius="6px"
                bg={viewType === 'faq' ? 'white' : 'transparent'}
                boxShadow={viewType === 'faq' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'}
                cursor="pointer"
                transition="all 0.15s ease"
                _hover={{ bg: viewType === 'faq' ? 'white' : 'rgba(255,255,255,0.5)' }}
              >
                <Text
                  fontSize="13px"
                  fontWeight={viewType === 'faq' ? 600 : 500}
                  color={viewType === 'faq' ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid}
                >
                  Inspect
                </Text>
              </Box>
              {isAgentWatch && (
                <Box
                  onClick={() => {
                    setViewType('steer');
                    setConversationId(undefined);
                  }}
                  px={{ base: 3, sm: 4 }}
                  py={1.5}
                  borderRadius="6px"
                  bg={viewType === 'steer' ? 'white' : 'transparent'}
                  boxShadow={viewType === 'steer' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'}
                  cursor="pointer"
                  transition="all 0.15s ease"
                  _hover={{ bg: viewType === 'steer' ? 'white' : 'rgba(255,255,255,0.5)' }}
                >
                  <Text
                    fontSize="13px"
                    fontWeight={viewType === 'steer' ? 600 : 500}
                    color={viewType === 'steer' ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid}
                  >
                    Steer
                  </Text>
                </Box>
              )}
            </HStack>

            {/* Persona search */}
            {viewType === 'overall' && (
              <Box position="relative" width={{ base: '100%', sm: 'auto' }}>
                <InputGroup size="sm" width={{ base: '100%', sm: '220px' }}>
                  <InputLeftElement pointerEvents="none">
                    <IoSearch color={HelivanColor.text.blueMid} />
                  </InputLeftElement>
                  <Input
                    ref={searchInputRef}
                    placeholder="Search..."
                    value={personaSearchQuery}
                    onChange={(e) => {
                      setPersonaSearchQuery(e.target.value);
                      setShowPersonaDropdown(true);
                    }}
                    onFocus={() => setShowPersonaDropdown(true)}
                    onBlur={() => {
                      // Delay to allow click on dropdown item
                      setTimeout(() => setShowPersonaDropdown(false), 200);
                    }}
                    borderRadius="6px"
                    bg="white"
                    border="1px solid"
                    borderColor={HelivanColor.border.primary}
                    _placeholder={{ color: HelivanColor.text.gray }}
                    _focus={{
                      borderColor: HelivanColor.text.helivanBlue,
                      boxShadow: 'none',
                    }}
                  />
                </InputGroup>
                {/* Search results dropdown */}
                {showPersonaDropdown && filteredPersonas.length > 0 && (
                  <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    minWidth={{ base: 'calc(100vw - 32px)', sm: '280px' }}
                    maxWidth={{ base: 'calc(100vw - 32px)', sm: 'none' }}
                    mt={1}
                    bg="white"
                    borderRadius="6px"
                    boxShadow="lg"
                    border="1px solid"
                    borderColor={HelivanColor.border.primary}
                    zIndex={100}
                  >
                    <Box maxHeight="300px" overflowY="auto">
                      {filteredPersonas.map((persona) => {
                        const isSelected = Array.from(selectedTwins).some(
                          (t) => t.id === persona.id
                        );
                        return (
                          <Box
                            key={persona.id}
                            px={3}
                            py={2}
                            cursor="pointer"
                            bg={isSelected ? HelivanColor.hover.lightBlue : 'white'}
                            _hover={{ bg: HelivanColor.hover.lightBlue }}
                            onClick={(e) => {
                              const isMultiSelect = e.ctrlKey || e.metaKey;
                              // Toggle selection
                              setSelectedTwins((prev) => {
                                const newSet = new Set(prev);
                                const existing = Array.from(newSet).find(
                                  (t) => t.id === persona.id
                                );
                                if (existing) {
                                  newSet.delete(existing);
                                } else {
                                  newSet.add({ id: persona.id, name: persona.name });
                                }
                                return newSet;
                              });
                              // Only close dropdown on regular click, keep open for multi-select
                              if (!isMultiSelect) {
                                setPersonaSearchQuery('');
                                setShowPersonaDropdown(false);
                              }
                            }}
                          >
                            <Text fontSize="13px" color={HelivanColor.text.blueDark}>
                              {persona.name}
                              {isSelected && (
                                <Text as="span" ml={2} color={HelivanColor.text.helivanBlue}>
                                  ✓
                                </Text>
                              )}
                            </Text>
                          </Box>
                        );
                      })}
                    </Box>
                    {/* Multi-select hint */}
                    <Box
                      px={3}
                      py={2}
                      borderTop="1px solid"
                      borderColor={HelivanColor.border.primary}
                      bg={HelivanColor.background.secondary}
                    >
                      <Text fontSize="11px" color={HelivanColor.text.gray}>
                        Tip: Ctrl/Cmd + click on plot to multi-select
                      </Text>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Flex>

          {/* Collapsible filters panel */}
          <Collapse in={showFilters} animateOpacity>
            <Flex
              px={5}
              py={3}
              borderBottom="1px solid"
              borderBottomColor={HelivanColor.border.primary}
              bg={HelivanColor.background.secondary}
              alignItems="center"
              gap={4}
            >
              {viewType === 'overall' && (
                <>
                  <DemographicFilterSelect
                    dtType={project.digitalTwinType}
                    selectedDemographics={selectedDemographics}
                    setSelectedDemographics={setSelectedDemographics}
                  />
                  {hasTimeline && (
                    <HStack spacing={3} alignItems="center">
                      <FormControl display="flex" alignItems="center" width="auto">
                        <FormLabel htmlFor="show-movement" mb="0" mr={2}>
                          <Text fontSize="13px" color={HelivanColor.text.blueMid}>
                            Show movement
                          </Text>
                        </FormLabel>
                        <Switch
                          id="show-movement"
                          size="sm"
                          isChecked={showMovement}
                          onChange={(e) => setShowMovement(e.target.checked)}
                          colorScheme="blue"
                        />
                      </FormControl>
                      {showMovement && (
                        <Menu isLazy>
                          <MenuButton
                            as={Button}
                            rightIcon={<MdOutlineKeyboardArrowDown size={14} />}
                            size="sm"
                            variant="outline"
                            bg="white"
                            border="1px solid"
                            borderColor={HelivanColor.border.primary}
                            borderRadius="6px"
                            fontWeight={500}
                            fontSize="13px"
                            color={HelivanColor.text.blueDark}
                            _hover={{ bg: HelivanColor.background.secondary }}
                            _active={{ bg: HelivanColor.background.secondary }}
                          >
                            Compare to: {formatDateShort(
                              project.timelineProjects?.find((p) => p.id === comparisonProjectId)?.dateCreated ?? project.createdAt
                            )}
                          </MenuButton>
                          <Portal>
                            <MenuList
                              maxHeight="300px"
                              overflowY="auto"
                              zIndex={1400}
                              boxShadow="0 4px 20px rgba(0,0,0,0.15)"
                              borderRadius="8px"
                              border="1px solid"
                              borderColor={HelivanColor.border.primary}
                              py={2}
                            >
                              {project.timelineProjects?.filter((tp) => tp.id !== project.id).map((timelineProject, idx) => (
                                <MenuItem
                                  key={timelineProject.id}
                                  onClick={() => setComparisonProjectId(timelineProject.id)}
                                  bg={comparisonProjectId === timelineProject.id ? HelivanColor.hover.lightBlue : undefined}
                                  fontSize="13px"
                                  py={2}
                                  px={3}
                                  _hover={{ bg: HelivanColor.background.secondary }}
                                >
                                  {formatDateShort(timelineProject.dateCreated)} {idx === 0 ? '(oldest)' : ''}
                                </MenuItem>
                              ))}
                            </MenuList>
                          </Portal>
                        </Menu>
                      )}
                    </HStack>
                  )}
                </>
              )}
              {viewType !== 'overall' && (
                <Text fontSize="13px" color={HelivanColor.text.blueMid}>
                  No filters available for this view
                </Text>
              )}
            </Flex>
          </Collapse>
          {/* Failure banner for FAILED or COMPLETED with 0 perspectives */}
          {(data?.project?.status === ProjectStatus.Failed ||
            (data?.project?.status === ProjectStatus.Completed &&
              (perspectives ?? []).length === 0)) && (
            <Flex
              px={5}
              py={4}
              bg="red.50"
              borderBottom="1px solid"
              borderColor="red.200"
              alignItems="center"
              gap={3}
            >
              <Icon as={IoAlertCircleOutline} color="red.500" boxSize={5} flexShrink={0} />
              <Box flex={1}>
                <Text fontSize="14px" fontWeight={600} color="red.700">
                  Project generation failed
                </Text>
                {data?.project?.errorMessage && (
                  <Text fontSize="13px" color="red.600" mt={1} noOfLines={2}>
                    {data.project.errorMessage}
                  </Text>
                )}
                {!data?.project?.errorMessage &&
                  data?.project?.status === ProjectStatus.Completed &&
                  (perspectives ?? []).length === 0 && (
                  <Text fontSize="13px" color="red.600" mt={1}>
                    Generation completed but produced no perspectives. Agents may have been offline.
                  </Text>
                )}
              </Box>
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                leftIcon={<MdRefresh size={16} />}
                onClick={async () => {
                  try {
                    await retryProject({
                      variables: { projectId: project.id },
                    });
                    toast({
                      title: 'Retry started',
                      description: 'Project generation has been restarted.',
                      status: 'success',
                      duration: 5000,
                    });
                  } catch (error) {
                    toast({
                      title: 'Retry failed',
                      description: error instanceof Error ? error.message : 'Failed to retry project',
                      status: 'error',
                      duration: 5000,
                    });
                  }
                }}
                isLoading={retryLoading}
                loadingText="Retrying..."
                flexShrink={0}
              >
                Retry
              </Button>
            </Flex>
          )}
          {/* Main content scroll area */}
          <Flex
            width="full"
            overflow="auto"
            flex={1}
            py={4}
            ref={scrollContainerRef}
          >
            <Flex width="full" height="full" px={5} justifyContent="center">
              {viewType === 'overall' ? (
                <ProjectTabViewOverall
                  allTwins={allDigitalTwins}
                  projectType={project.type ?? ProjectType.Explorable}
                  selectedTwins={selectedTwins}
                  setSelectedTwins={handleSetSelectedTwins}
                  setDigitalTwinId={handleSetDigitalTwinId}
                  digitalTwinId={digitalTwinId}
                  topic={'Opinion Geometry Overview'}
                  perspectives={perspectives ?? []}
                  selectedDemographics={selectedDemographics}
                  modelCardData={modelCardData}
                  subtopics={subtopics ?? []}
                  setPlotRef={setPlotRef}
                  digitalTwinType={project.digitalTwinType}
                  showMovement={showMovement}
                  comparisonPerspectives={
                    showMovement
                      ? (comparisonProjectData?.project?.perspectives ?? [])
                      : []
                  }
                  timelineProjects={project.timelineProjects ?? []}
                  currentProjectId={project.id}
                  comparisonProjectId={comparisonProjectId}
                  allTimelinePerspectives={allTimelinePerspectives}
                  requestTimelineData={requestTimelineData}
                  initialTabIndex={initialTabIndex}
                  changeDetectionEnabled={settingsData?.getProjectSettings?.changeDetectionEnabled ?? true}
                  sensitivityStdDevs={settingsData?.getProjectSettings?.sensitivityStdDevs ?? 2.0}
                  burnInPeriod={settingsData?.getProjectSettings?.burnInPeriod ?? 10}
                />
              ) : viewType === 'chat' ? (
                <ProjectTabViewChat
                  perspectives={perspectives ?? []}
                  topic={project.topic}
                  conversationId={conversationId}
                  createNew={createNewChat}
                  setCreateNew={setCreateNewChat}
                  chatInitialType={chatFilter}
                  allPersonas={allDigitalTwins}
                  setConversationId={setConversationId}
                  projectId={project.id}
                  dtType={project.digitalTwinType}
                  modelCardData={modelCardData}
                  allChats={data?.getRecentConversationsBasicData ?? []}
                  setSelectedTwins={handleSetSelectedTwins}
                  setDigitalTwinId={handleSetDigitalTwinId}
                />
              ) : viewType === 'faq' ? (
                project.type === ProjectType.PlanningProbe ? (
                  <PlanningInspect
                    allDigitalTwins={allDigitalTwins}
                    digitalTwinId={digitalTwinId}
                    setDigitalTwinId={setDigitalTwinId}
                    perspectives={perspectives ?? []}
                    projectId={project.id}
                    timelineProjects={project.timelineProjects ?? []}
                    evalTokens={{
                      snapshot: (project.evalInputTokens ?? 0) + (project.evalOutputTokens ?? 0),
                      cumulative: (project.timelineProjects ?? []).reduce(
                        (sum, v) => sum + (v.evalInputTokens ?? 0) + (v.evalOutputTokens ?? 0),
                        0,
                      ),
                    }}
                  />
                ) : (
                  <PersonalInspect
                    allDigitalTwins={allDigitalTwins}
                    digitalTwinId={digitalTwinId}
                    setDigitalTwinId={setDigitalTwinId}
                    perspectives={perspectives ?? []}
                    projectId={project.id}
                    timelineProjects={project.timelineProjects ?? []}
                  />
                )
              ) : (
                // Steer — extensible hub of steering tools (first tool: /compact optimizer)
                <SteerHub
                  agentId={allDigitalTwins[0]?.id}
                  sessionId={project.sessionId}
                  taskCount={project.subtopics?.length}
                  timelineProjects={project.timelineProjects ?? undefined}
                />
              )}
            </Flex>
          </Flex>
        </Stack>

        {/* Collapsible Right Sidebar — in-flow on desktop, overlay drawer on mobile */}
        {showSidebar && (
          <>
            <Flex height="full" width="1px" bg={HelivanColor.border.primary} display={{ base: 'none', lg: 'block' }} />
            <Box
              position={{ base: 'absolute', lg: 'relative' }}
              right={0}
              top={0}
              zIndex={{ base: 20, lg: 'auto' }}
              boxShadow={{ base: '-6px 0 18px rgba(0,0,0,0.14)', lg: 'none' }}
              width={{ base: '85vw', lg: '280px' }}
              minWidth={{ base: 'unset', lg: '280px' }}
              maxWidth={{ base: '340px', lg: '280px' }}
              height="full"
              overflowY="auto"
              flexShrink={0}
              bg="white"
            >
              <Stack p={5} spacing={4} alignItems="start" minHeight="min-content">
            {/* Selected Personas */}
            <Box width="full">
              <HelivanText variant="largeSubheader">Selection</HelivanText>
              {selectedTwinsWithData.length > 0 ? (
                <Wrap spacing={2} mt={3}>
                  {selectedTwinsWithData.map((twin) => {
                    const socialLinks = [];
                    const dt = twin.digitalTwin;
                    const twitterHandle =
                      dt?.congressData?.twitterHandle ||
                      dt?.earlyStageVcData?.twitterHandle ||
                      dt?.fortuneCeoData?.twitterHandle ||
                      dt?.sendrilData?.twitterHandle;
                    const instagramHandle = dt?.sendrilData?.instagramHandle;
                    const tiktokHandle = dt?.sendrilData?.tiktokHandle;
                    const youtubeHandle = dt?.sendrilData?.youtubeHandle;
                    const facebookHandle = dt?.sendrilData?.facebookHandle;
                    const substackHandle = dt?.sendrilData?.substackHandle;

                    if (twitterHandle) socialLinks.push({ type: 'x', handle: twitterHandle, icon: BsTwitterX, url: `https://x.com/${twitterHandle}` });
                    if (instagramHandle) socialLinks.push({ type: 'instagram', handle: instagramHandle, icon: FaInstagram, url: `https://instagram.com/${instagramHandle}` });
                    if (tiktokHandle) socialLinks.push({ type: 'tiktok', handle: tiktokHandle, icon: FaTiktok, url: `https://tiktok.com/@${tiktokHandle}` });
                    if (youtubeHandle) socialLinks.push({ type: 'youtube', handle: youtubeHandle, icon: FaYoutube, url: `https://youtube.com/@${youtubeHandle}` });
                    if (facebookHandle) socialLinks.push({ type: 'facebook', handle: facebookHandle, icon: FaFacebook, url: `https://facebook.com/${facebookHandle}` });
                    if (substackHandle) socialLinks.push({ type: 'substack', handle: substackHandle, icon: SiSubstack, url: `https://${substackHandle}.substack.com` });

                    return (
                      <WrapItem key={twin.id}>
                        <Popover trigger="hover" placement="bottom-start">
                          <PopoverTrigger>
                            <Tag
                              size="md"
                              borderRadius="full"
                              variant="solid"
                              bg={twin.color}
                              cursor="pointer"
                            >
                              <TagLabel>{twin.name}</TagLabel>
                              <TagCloseButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedTwins((prev) => {
                                    const newSet = new Set(prev);
                                    const existing = Array.from(newSet).find(
                                      (t) => t.id === twin.id
                                    );
                                    if (existing) newSet.delete(existing);
                                    return newSet;
                                  });
                                }}
                              />
                            </Tag>
                          </PopoverTrigger>
                          <Portal>
                            <PopoverContent width={{ base: 'calc(100vw - 32px)', sm: '260px' }} maxWidth="260px">
                              <PopoverBody p={3}>
                                <Stack spacing={2}>
                                  <Text fontWeight={600} fontSize="14px" color={HelivanColor.text.blueDark}>
                                    {twin.name}
                                  </Text>
                                  {twin.digitalTwin && <DigitalTwinMetadata size="sm" digitalTwin={twin.digitalTwin} />}
                                  {socialLinks.length > 0 && (
                                    <>
                                      <Divider my={1} />
                                      <HStack spacing={3}>
                                        {socialLinks.map((link) => (
                                          <Tooltip key={link.type} label={`@${link.handle}`} placement="top">
                                            <Link href={link.url} isExternal>
                                              <Icon
                                                as={link.icon}
                                                boxSize={4}
                                                color={HelivanColor.text.blueMid}
                                                _hover={{ color: HelivanColor.text.helivanBlue }}
                                              />
                                            </Link>
                                          </Tooltip>
                                        ))}
                                      </HStack>
                                    </>
                                  )}
                                </Stack>
                              </PopoverBody>
                            </PopoverContent>
                          </Portal>
                        </Popover>
                      </WrapItem>
                    );
                  })}
                </Wrap>
              ) : (
                <Text fontSize="13px" color={HelivanColor.text.gray} mt={2}>
                  Nothing selected. Use the search bar or click on the plot to select.
                </Text>
              )}
            </Box>
            <Divider />
            <Flex
              justifyContent="space-between"
              width="full"
              alignItems="center"
            >
              <HelivanText variant="largeSubheader">
                Subtopics
              </HelivanText>
              <IconButton
                icon={
                  mapCollapse.isOpen ? (
                    <MdOutlineKeyboardArrowDown size={24} />
                  ) : (
                    <MdOutlineKeyboardArrowRight size={24} />
                  )
                }
                onClick={mapCollapse.onToggle}
                aria-label={mapCollapse.isOpen ? 'Collapse' : 'Expand'}
                size="sm"
                variant="ghost"
                _hover={{ background: undefined }}
              />
            </Flex>
            <Collapse in={mapCollapse.isOpen} animateOpacity>
              <Stack spacing={5}>
                {subtopics?.map((subtopic, idx) => {
                  return (
                    <Box
                      key={idx}
                      width="fit-content"
                      onClick={() => {
                        setViewType('overall');
                        scrollToPlot(`subtopic-${subtopic.name}`);
                      }}
                      px="6px"
                      borderRadius="4px"
                      py="2px"
                      bg={
                        activeSection === `subtopic-${subtopic.name}`
                          ? HelivanColor.hover.lightBlue
                          : 'white'
                        // todo this should be true only when the person is currently viewing the projectPlot of that subtopic
                      }
                      _hover={{ bg: HelivanColor.hover.lightBlue }}
                      cursor="pointer"
                    >
                      <HelivanText variant="smallBody">
                        {subtopic.name}
                      </HelivanText>
                    </Box>
                  );
                })}
                <Box
                  onClick={() => {
                    setViewType('overall');
                    scrollToPlot(`overall`);
                  }}
                  width="fit-content"
                  px="6px"
                  py="2px"
                  borderRadius="4px"
                  bg={
                    activeSection === 'overall'
                      ? HelivanColor.hover.lightBlue
                      : 'white'
                  }
                  _hover={{ bg: HelivanColor.hover.lightBlue }}
                  cursor="pointer"
                >
                  <HelivanText variant="smallBody">Overall</HelivanText>
                </Box>
              </Stack>
            </Collapse>
          </Stack>
            </Box>
          </>
        )}
      </Flex>
    </Flex>
  );
};
